import { Link } from 'react-router-dom';
import { usePoolData } from '@/hooks/usePoolData';
import { useAllIncentivesWithAPR, IncentiveWithAPR } from '@/hooks/useAllIncentivesWithAPR';
import { useIncentiveAPR } from '@/hooks/useIncentiveApr';
import { WalletConnection } from '@/components/WalletConnection';
import { UI_CONFIG, TOKEN_LOGOS } from '@/config/constants';

function StatsCard({
  label,
  value,
  subtext,
}: {
  label: string;
  value: string;
  subtext?: string;
}) {
  return (
    <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 border border-white/40 shadow-lg">
      <p className="text-slate-500 text-sm font-medium mb-1">{label}</p>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
      {subtext && <p className="text-slate-500 text-xs mt-1">{subtext}</p>}
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 border border-white/40 shadow-lg hover:shadow-xl transition-shadow">
      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 text-white">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({
  step,
  title,
  description,
}: {
  step: number;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start space-x-4">
      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
        {step}
      </div>
      <div>
        <h4 className="text-lg font-semibold text-slate-900 mb-1">{title}</h4>
        <p className="text-slate-600 text-sm">{description}</p>
      </div>
    </div>
  );
}

function IncentiveCard({
  incentive,
  isDummy = false,
}: {
  incentive: IncentiveWithAPR;
  isDummy?: boolean;
}) {
  // Use the shared hook for real APR calculation (each incentive has its own staked value)
  const realApr = useIncentiveAPR(isDummy ? '' : incentive.incentiveId);

  const formatAPR = (apr: number | string): string => {
    const aprNum = typeof apr === 'string' ? parseFloat(apr) : apr;
    if (isNaN(aprNum) || aprNum === 0) return '---';
    if (aprNum >= 1000) return `${Math.round(aprNum).toLocaleString()}%`;
    if (aprNum >= 100) return `${aprNum.toFixed(1)}%`;
    return `${aprNum.toFixed(2)}%`;
  };

  // Use real APR from hook, fallback to incentive.apr for dummy data
  const displayApr = isDummy ? incentive.apr : realApr;

  return (
    <div className="bg-white/60 backdrop-blur-xl rounded-xl p-4 border border-white/40 shadow-lg">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-slate-500">
            {incentive.reward.toLocaleString()} JOCX Rewards
          </div>
          <div className="text-xs text-slate-400">
            Ends: {incentive.endTime.toLocaleDateString()}
          </div>
        </div>
        <div className="text-2xl font-bold text-slate-700">
          {formatAPR(displayApr)}
          <span className="text-sm font-medium text-slate-500 ml-1">APR</span>
        </div>
      </div>
    </div>
  );
}

// Dummy incentives for display when real data is not available
const DUMMY_INCENTIVES: IncentiveWithAPR[] = [
  {
    incentive: {} as any,
    incentiveId: 'dummy-1',
    apr: 125.5,
    aprRate: 1,
    isLive: true,
    reward: 50000,
    endTime: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
  },
  {
    incentive: {} as any,
    incentiveId: 'dummy-2',
    apr: 85.2,
    aprRate: 0.8,
    isLive: true,
    reward: 30000,
    endTime: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000), // 60 days from now
  },
];

export default function Landing() {
  const poolData = usePoolData();
  const { incentives, isLoading } = useAllIncentivesWithAPR();

  // Get live incentives only for display
  // Use dummy data only in testnet/development, not in production
  const isTestnet = process.env.REACT_APP_IS_TESTNET === 'true';
  const realLiveIncentives = incentives.filter(i => i.isLive);
  const isDummyData = realLiveIncentives.length === 0 && isTestnet;
  const liveIncentives = isDummyData ? DUMMY_INCENTIVES : realLiveIncentives;

  const displayPrice = poolData?.jocxPrice
    ? `$${poolData.jocxPrice.toFixed(4)}`
    : '$0.0234';
  const displayTVL = '$125,000'; // Placeholder

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 relative overflow-hidden">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-2xl border-b border-white/30 sticky top-0 z-50 shadow-xl shadow-slate-200/30">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                {UI_CONFIG.APP_NAME}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="https://japanopenchain.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-slate-900 text-sm font-medium hidden sm:block"
              >
                About JOC
              </a>
              <WalletConnection />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl" />
          <div className="absolute top-40 right-20 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Token icons */}
            <div className="flex justify-center items-center space-x-2 mb-8">
              <img
                src={TOKEN_LOGOS.JOCX}
                alt="JOCX"
                className="w-16 h-16 rounded-full shadow-lg"
              />
              <span className="text-4xl text-slate-300">+</span>
              <img
                src={TOKEN_LOGOS.USDT}
                alt="USDT"
                className="w-16 h-16 rounded-full shadow-lg"
              />
            </div>

            <h1 className="text-5xl sm:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Earn{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                JOCX Rewards
              </span>
              <br />
              by Providing Liquidity
            </h1>

            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Stake your JOCX/USDT liquidity position on Uniswap V3 and earn
              additional JOCX token rewards. Powered by Japan Open Chain.
            </p>

            {/* Incentive List */}
            {liveIncentives.length > 0 && (
              <div className="max-w-xl mx-auto mb-10 space-y-3">
                <p className="text-sm text-slate-500 mb-2">Available Reward Programs ({liveIncentives.length})</p>
                {liveIncentives.map((inc) => (
                  <IncentiveCard
                    key={inc.incentiveId}
                    incentive={inc}
                    isDummy={isDummyData}
                  />
                ))}
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <Link
                to="/app"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all text-lg"
              >
                Start Earning
              </Link>
              <a
                href="#how-it-works"
                className="px-8 py-4 bg-white/80 text-slate-700 font-semibold rounded-xl border border-slate-200 hover:bg-white transition-all text-lg"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white/40 backdrop-blur-xl border-y border-white/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatsCard
              label="Total Staked"
              value={displayTVL}
              subtext="In active incentives"
            />
            <StatsCard
              label="JOCX Price"
              value={displayPrice}
              subtext="Live price"
            />
            <StatsCard
              label="Pool"
              value="JOCX/USDT"
              subtext="Uniswap V3 - 0.3%"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Start earning JOCX rewards in just a few simple steps
            </p>
          </div>

          <div className="max-w-2xl mx-auto space-y-8">
            <StepCard
              step={1}
              title="Connect Your Wallet"
              description="Connect your MetaMask or WalletConnect compatible wallet to get started."
            />
            <StepCard
              step={2}
              title="Add Liquidity"
              description="Provide JOCX and USDT to the Uniswap V3 pool. Use our One-Click flow for easy setup."
            />
            <StepCard
              step={3}
              title="Stake Your Position"
              description="Stake your LP NFT to start earning JOCX rewards automatically."
            />
            <StepCard
              step={4}
              title="Claim Rewards"
              description="Unstake anytime and claim your accumulated JOCX rewards."
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white/40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Why Stake with X-Staker?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              }
              title="Secure & Audited"
              description="Built on Uniswap's official V3 Staker contract. Your funds are always under your control."
            />
            <FeatureCard
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              }
              title="One-Click Staking"
              description="Our Beginner Mode handles everything - swap, add liquidity, and stake in one transaction flow."
            />
            <FeatureCard
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              title="JOCX Rewards"
              description="Earn JOCX tokens as staking rewards on top of Uniswap trading fees."
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 border border-white/40">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                What is Impermanent Loss?
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Impermanent Loss (IL) occurs when the price ratio of tokens in a
                liquidity pool changes. The high APY from staking rewards is
                designed to offset potential IL. For a 50% price change, IL is
                typically around 2%.
              </p>
            </div>

            <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 border border-white/40">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Is my fund safe?
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Your liquidity is staked in Uniswap's official V3 Staker
                contract (audited). You maintain full custody - you can unstake
                and withdraw your NFT position anytime.
              </p>
            </div>

            <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 border border-white/40">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                How are rewards calculated?
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Rewards are distributed based on your share of total staked
                liquidity and the time your position is staked. The APR shown is
                annualized based on current conditions.
              </p>
            </div>

            <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 border border-white/40">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                What tokens do I need?
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                You can start with USDT, USDC, or ETH. Our One-Click flow will
                automatically swap and add liquidity for you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Start Earning?
          </h2>
          <p className="text-blue-100 mb-10 text-lg">
            Connect your wallet and start earning JOCX rewards today.
          </p>
          <Link
            to="/app"
            className="inline-flex px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all text-lg"
          >
            Launch App
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-6 md:mb-0">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <span className="text-white font-semibold">
                {UI_CONFIG.APP_NAME}
              </span>
            </div>

            <div className="flex items-center space-x-6">
              <a
                href="https://japanopenchain.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white text-sm transition-colors"
              >
                Japan Open Chain
              </a>
              <a
                href="https://app.uniswap.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white text-sm transition-colors"
              >
                Uniswap
              </a>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center">
            <p className="text-slate-500 text-sm">
              Powered by Japan Open Chain Foundation
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
